import { Box3, Vector3 } from 'three';

interface ParsedGCode {
  rapidMovePoints: Vector3[];
  rapidSegments: Vector3[][];
  linearMovePoints: Vector3[];
  linearSegments: Vector3[][]; // Array of continuous line segments
}

enum GCodeUnit {
  inches = 'inches',
  mm = 'mm',
}

// Helper function to interpolate arc between two points
function interpolateArc(
  startX: number,
  startY: number,
  startZ: number,
  endX: number,
  endY: number,
  endZ: number,
  centerOffsetX: number,
  centerOffsetY: number,
  isClockwise: boolean,
  segments: number = 20
): Vector3[] {
  const points: Vector3[] = [];

  // Calculate center point
  const centerX = startX + centerOffsetX;
  const centerY = startY + centerOffsetY;

  // Calculate radius
  const radius = Math.sqrt(
    centerOffsetX * centerOffsetX + centerOffsetY * centerOffsetY
  );

  // Calculate start and end angles
  const startAngle = Math.atan2(startY - centerY, startX - centerX);
  const endAngle = Math.atan2(endY - centerY, endX - centerX);

  // Calculate angular difference
  let angleDiff = endAngle - startAngle;

  // Adjust for direction and wraparound
  if (isClockwise) {
    if (angleDiff >= 0) angleDiff -= 2 * Math.PI;
  } else if (angleDiff <= 0) angleDiff += 2 * Math.PI;

  // Interpolate points along the arc
  for (let i = 0; i <= segments; i += 1) {
    const t = i / segments;
    const angle = startAngle + angleDiff * t;
    const z = startZ + (endZ - startZ) * t;

    const px = centerX + radius * Math.cos(angle);
    const py = centerY + radius * Math.sin(angle);

    points.push(new Vector3(px, py, z));
  }

  return points;
}

export function parseGCode(gcode: string): ParsedGCode {
  const lines = gcode.split('\n');
  const rapidMovePoints: Vector3[] = []; // Keep for backward compatibility if needed, or we can just use segments
  const rapidSegments: Vector3[][] = []; // Store pairs of [start, end] for rapid moves
  const linearMovePoints: Vector3[] = [];
  const linearSegments: Vector3[][] = [];

  let x = 0;
  let y = 0;
  let z = 0;

  let relative = false; // Positioning mode (modal): G90 absolute (default) / G91 incremental
  let currentMotionMode = ''; // Track current motion mode (modal) - start empty
  let currentSegment: Vector3[] = [];
  let hasMovedFromOrigin = false; // Track if we've moved from the initial (0,0,0) position

  lines.forEach((line) => {
    // Trim whitespace and convert to uppercase for consistent parsing
    let trimmedLine = line.trim().toUpperCase();

    // Remove line numbers (N prefix) if present - e.g., "N5540 G02 X..." becomes "G02 X..."
    if (trimmedLine.match(/^N\d+\s+/)) {
      trimmedLine = trimmedLine.replace(/^N\d+\s+/, '');
    }

    // Check for G-code motion commands (modal commands persist until changed)
    // Match G0 or G00 (rapid move)
    if (
      /^G0*0(?:\s|[XYZIJKF]|$)/.test(trimmedLine) ||
      trimmedLine === 'G0' ||
      trimmedLine === 'G00'
    ) {
      currentMotionMode = 'G0';
    }
    // Match G1 or G01 (linear move)
    else if (
      /^G0*1(?:\s|[XYZIJKF]|$)/.test(trimmedLine) ||
      trimmedLine === 'G1' ||
      trimmedLine === 'G01'
    ) {
      currentMotionMode = 'G1';
    }
    // Match G2 or G02 (clockwise arc)
    else if (
      /^G0*2(?:\s|[XYZIJKF]|$)/.test(trimmedLine) ||
      trimmedLine === 'G2' ||
      trimmedLine === 'G02'
    ) {
      currentMotionMode = 'G2';
    }
    // Match G3 or G03 (counterclockwise arc)
    else if (
      /^G0*3(?:\s|[XYZIJKF]|$)/.test(trimmedLine) ||
      trimmedLine === 'G3' ||
      trimmedLine === 'G03'
    ) {
      currentMotionMode = 'G3';
    }

    // Positioning mode (modal): G90 absolute (default), G91 incremental.
    // LightBurn laser exports switch to G91 for the toolpath; without this,
    // every offset is read as an absolute coordinate and the path collapses
    // toward the origin instead of accumulating across the real design.
    // `(?!\.)` excludes G90.1/G91.1 (arc-center distance mode), which are a
    // different code and must not flip the motion positioning mode.
    if (/\bG90\b(?!\.)/.test(trimmedLine)) {
      relative = false;
    } else if (/\bG91\b(?!\.)/.test(trimmedLine)) {
      relative = true;
    }

    const matchX = line.match(/X(-?\d+(\.\d+)?)/i);
    const matchY = line.match(/Y(-?\d+(\.\d+)?)/i);
    const matchZ = line.match(/Z(-?\d+(\.\d+)?)/i);
    const matchI = line.match(/I(-?\d+(\.\d+)?)/i);
    const matchJ = line.match(/J(-?\d+(\.\d+)?)/i);

    let hasCoordinate = false;
    const oldX = x;
    const oldY = y;
    const oldZ = z;

    if (matchX) {
      x = relative ? x + parseFloat(matchX[1]) : parseFloat(matchX[1]);
      hasCoordinate = true;
    }
    if (matchY) {
      y = relative ? y + parseFloat(matchY[1]) : parseFloat(matchY[1]);
      hasCoordinate = true;
    }
    if (matchZ) {
      z = relative ? z + parseFloat(matchZ[1]) : parseFloat(matchZ[1]);
      hasCoordinate = true;
    }

    // Only process if we have new coordinates
    if (hasCoordinate) {
      const startPoint = new Vector3(oldX, oldY, oldZ);
      const endPoint = new Vector3(x, y, z);

      if (currentMotionMode === 'G0') {
        // Rapid move - end current cutting segment
        if (currentSegment.length > 0) {
          linearSegments.push(currentSegment);
          currentSegment = [];
        }
        rapidMovePoints.push(endPoint);

        // Only add rapid segment if we've actually moved from a real position
        // Avoid creating a line from origin (0,0,0) on the first rapid move
        if (hasMovedFromOrigin) {
          rapidSegments.push([startPoint, endPoint]);
        }
        hasMovedFromOrigin = true;
      } else if (currentMotionMode === 'G1') {
        // Linear move
        // If this is the start of a new segment, add the start point
        if (currentSegment.length === 0) {
          currentSegment.push(startPoint);
        }

        linearMovePoints.push(endPoint);
        currentSegment.push(endPoint);
        hasMovedFromOrigin = true;
      } else if (currentMotionMode === 'G2' || currentMotionMode === 'G3') {
        // Arc move - interpolate
        if (matchI || matchJ) {
          const i = matchI ? parseFloat(matchI[1]) : 0;
          const j = matchJ ? parseFloat(matchJ[1]) : 0;
          const isClockwise = currentMotionMode === 'G2';

          const arcPoints = interpolateArc(
            oldX,
            oldY,
            oldZ,
            x,
            y,
            z,
            i,
            j,
            isClockwise
          );

          // If this is the start of a new segment, ensure we have continuity
          // interpolateArc includes the start point, so we can just push all points
          // But if currentSegment is empty, we just push them.
          // If currentSegment is NOT empty, the last point should match the first point of arcPoints (approx),
          // so we might want to skip the first point of arcPoints to avoid duplicates?
          // interpolateArc returns [start, ..., end].

          if (currentSegment.length === 0) {
            // New segment, push all
            arcPoints.forEach((p) => {
              linearMovePoints.push(p);
              currentSegment.push(p);
            });
          } else {
            // Continuation, skip the first point as it should match currentSegment.last()
            // To be safe, we can just push all, or skip index 0.
            // Let's skip index 0 to avoid double points
            for (let k = 1; k < arcPoints.length; k += 1) {
              linearMovePoints.push(arcPoints[k]);
              currentSegment.push(arcPoints[k]);
            }
          }
          hasMovedFromOrigin = true;
        } else {
          // No I/J parameters, treat as linear
          if (currentSegment.length === 0) {
            currentSegment.push(startPoint);
          }
          linearMovePoints.push(endPoint);
          currentSegment.push(endPoint);
          hasMovedFromOrigin = true;
        }
      }
    }
  });

  // Don't forget to add the last segment if it exists
  if (currentSegment.length > 0) {
    linearSegments.push(currentSegment);
  }

  return { rapidMovePoints, rapidSegments, linearMovePoints, linearSegments };
}

export function getGcodeUnit(gcode: string): GCodeUnit {
  if (gcode.includes('G20')) {
    return GCodeUnit.inches;
  }
  if (gcode.includes('G21')) {
    return GCodeUnit.mm;
  }
  return GCodeUnit.mm;
}

export function calculateBoundingBox(points: Vector3[]): Box3 | null {
  if (points.length === 0) {
    return null;
  }

  const box = new Box3().setFromPoints(points);
  return box;
}

export function centerPoints(
  points: Vector3[],
  boundingBox: Box3 | null
): Vector3[] {
  if (!boundingBox) return points;

  const center = new Vector3();
  boundingBox.getCenter(center);

  return points.map((point) => point.clone().sub(center));
}

/** Grid cells sized to the part: 5-50 cells across it, sections every 10. */
export function gridScale(box: Box3 | null) {
  const size = box ? box.getSize(new Vector3()) : new Vector3();
  const span = Math.max(size.x, size.y, 10);
  const cellSize = 10 ** Math.floor(Math.log10(span / 5));
  return {
    cellSize,
    sectionSize: cellSize * 10,
    fadeDistance: span * 4,
    // parts are drawn centred on the origin; the grid is their floor
    position: [0, 0, -size.z / 2 - 0.01] as [number, number, number],
  };
}
